import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { and } from 'ember-truth-helpers';
import type { Hotel } from 'frontend-utils/models';
import { getLocalTime } from 'frontend-utils/utils/dayjs';
import GeneralButtonsButtonDayLiveshop from 'lumen/components/general/buttons/button-day-liveshop';
import type { Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { ParityType } from 'lumen/services/liveupdate';
import formatDate from 'lumen/utils/format-local-date';

export interface TdDateColumn {
  firstPos?: string;
  hasDayLiveShop: boolean;
  hotel: Hotel | null;
  parityType: ParityType;
  pos?: string;
}

interface ParityTablesCellsTdDateSignature {
  Args: {
    cellData: Period;
    column: TdDateColumn;
  };
}

export default class ParityTablesCellsTdDate extends Component<ParityTablesCellsTdDateSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];

  @tracked isLiveupdateTriggered: boolean = false;

  get dateFormatted(): string {
    const { cellData } = this.args;

    if (cellData.day.date) {
      return formatDate(cellData.day.date);
    }

    return this.intl.t('general.missing');
  }

  get hotel(): Hotel {
    const { column } = this.args;

    return column.hotel ?? this.hotelAndMarketService.hotel;
  }

  get isDateHidden(): boolean {
    const { cellData, column } = this.args;

    return column.firstPos !== undefined && column.firstPos !== cellData.pos;
  }

  get isDayLiveupdateTriggerAllowed(): boolean {
    const { column } = this.args;

    if (column.hasDayLiveShop) {
      return !this.isUpdating;
    }

    return false;
  }

  get isDayLiveupdateTriggerVisible(): boolean {
    return this.isLiveupdateTriggered && !this.isPartialLiveupdateOngoing;
  }

  get isPartialLiveupdateOngoing(): boolean {
    const { partialLiveupdatesForThisSelection } = this.liveupdate;

    if (partialLiveupdatesForThisSelection) {
      return partialLiveupdatesForThisSelection.length > 0;
    }

    return false;
  }

  get isPast(): boolean {
    const { cellData } = this.args;

    if (cellData.day.date) {
      return cellData.day.date.isBefore(
        getLocalTime(`${this.hotel.hotelinfo.timezone}`),
        'day',
      );
    }

    return false;
  }

  get isToday(): boolean {
    const { cellData } = this.args;

    if (cellData.day.date) {
      return cellData.day.date.isSame(
        getLocalTime(`${this.hotel.hotelinfo.timezone}`),
        'day',
      );
    }

    return false;
  }

  get isUpdating(): boolean {
    const { cellData, column } = this.args;

    if (column.pos) {
      return this.liveupdate.isPOSParityUpdating(
        cellData.fromDate,
        cellData.day.date,
        cellData.parities ?? [],
        column.pos,
      );
    }

    return this.liveupdate.isDefaultParityUpdating(
      cellData.fromDate,
      cellData.day.date,
      cellData.parities ?? [],
    );
  }

  @action startDayLiveupdate(): void {
    const { cellData, column } = this.args;

    if (this.liveupdate.isDayLiveupdateLimitReached) {
      return;
    }

    this.liveupdate.startLiveParityShop({
      dayShop: cellData.day?.date.format('YYYY-MM-DD'),
      parityType: column.parityType,
    });
  }

  @action updateIsLiveupdateTriggered(value: boolean): void {
    this.isLiveupdateTriggered = value;
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class="flex items-center
        {{if this.isPast 'td-past'}}{{if this.isToday 'td-today'}}
        flex justify-between"
      role="button"
      {{on "mouseenter" (fn this.updateIsLiveupdateTriggered true)}}
      {{on "mouseleave" (fn this.updateIsLiveupdateTriggered false)}}
    >
      <p
        class="truncate
          {{if this.isToday 'td-today-date'}}
          {{if this.isDateHidden 'invisible'}}"
      >
        {{this.dateFormatted}}
      </p>

      <GeneralButtonsButtonDayLiveshop
        @isDayLiveupdateLimitReached={{this.liveupdate.isDayLiveupdateLimitReached}}
        @isUpdating={{this.isUpdating}}
        @isVisible={{and
          this.isDayLiveupdateTriggerVisible
          this.isDayLiveupdateTriggerAllowed
        }}
        @startDayLiveupdate={{this.startDayLiveupdate}}
      />
    </div>
  </template>
}
